import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { SalaryPeriodSelect } from "../workTimeBeta/muiSalaryPeriodSelect";
import { ISalaryPeriodItem } from "../../models/salaryPeriod/salaryPeriodItem";
import { useFetch } from "../../hooks/useFetch";
import { updateWorkShiftPlanningFilters } from "../../store/workShiftPlanningSlice";
import { getSalaryPeriodTypesWithPeriods } from "../../services/salaryPeriodService";
import { Base } from "../../framework/base";
import { Autocomplete, FormControl, TextField, Chip } from "@mui/material";
import { employeeGroupListItemDto } from "../../models/employeeGroup/employeeGroup";
import { getEmployeeGroups } from "../../services/employeeGroupService";
import { useTranslation } from "react-i18next";
import MuiSelect from "../framework/muiSelect";
import { SalaryPeriodTypeDto } from "../../models/salaryPeriodType/salaryPeriodType";
import { Switch, FormControlLabel } from "@mui/material";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { useParams } from "react-router-dom";
import { WorkShiftPlanningParams, WorkShiftPlanningTab } from "./WorkShiftPlanningRouter";

export const WorkShiftPlanningFilters = ({ employees }: { employees: EmployeeListItemDto[] }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [employeeGroups, setEmployeeGroups] = useState<employeeGroupListItemDto[]>();
    const [salaryPeriodTypes, setSalaryPeriodTypes] =
        useState<SalaryPeriodTypeDto[]>();
    const { tab } = useParams<WorkShiftPlanningParams>();
    const isEmployeeTab = tab === WorkShiftPlanningTab.Employee;
    const { selectedEmployeeGroupId, selectedSalaryPeriodTypeId, selectedSalaryPeriodId, selectedEmployeeIds } =
        useAppSelector((state) => state.workShiftPlanning.filters);

    const salaryPeriods = useMemo(
        () =>
            salaryPeriodTypes?.find((t) => t.id === selectedSalaryPeriodTypeId)
                ?.salaryPeriods,
        [salaryPeriodTypes, selectedSalaryPeriodTypeId]
    );

    const selectedSalaryPeriod = useMemo(
        () => salaryPeriods?.find((p) => p.id === selectedSalaryPeriodId),
        [selectedSalaryPeriodId, salaryPeriods]
    );

    const selectedEmployeeGroup = useMemo(
        () => employeeGroups?.find((g) => g.id === selectedEmployeeGroupId),
        [selectedEmployeeGroupId, employeeGroups]
    );

    const groupEmployees = useAppSelector(
        (state) => state.employees?.groupEmployees ?? []
    );
    const groupEmployeesGroupId = useAppSelector(
        (state) => state.employees?.groupEmployeesGroupId ?? null
    );

    const employeeOptions = useMemo(() => {
        if (selectedEmployeeGroupId) {
            return groupEmployeesGroupId === selectedEmployeeGroupId
                ? groupEmployees
                : [];
        }

        return employees ?? [];
    }, [selectedEmployeeGroupId, groupEmployeesGroupId, groupEmployees, employees]);

    const selectedEmployees = useMemo(
        () => employeeOptions?.filter((employee) => selectedEmployeeIds?.includes(employee.id)),
        [selectedEmployeeIds, employeeOptions]
    );

    const showUnemployable = useAppSelector(
        (state) => state.workShiftPlanning.filters.showUnemployable
    );
    const handleShowUnemployableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(updateWorkShiftPlanningFilters({ showUnemployable: event.target.checked }));
    };
    const showAllDayBookings = useAppSelector(
        (state) => !!state.workShiftPlanning.filters.showAllDayBookings
    );
    const handleShowAllDayBookingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(updateWorkShiftPlanningFilters({ showAllDayBookings: event.target.checked }));
    };

    useFetch(
        (signal) => getSalaryPeriodTypesWithPeriods(signal),
        {
            onSuccess: setSalaryPeriodTypes,
        },
        []
    );

    useFetch(
        (signal) => getEmployeeGroups(true, signal),
        {
            onSuccess: (res) =>
                setEmployeeGroups(
                    res.data.filter((d) => !!d.salaryPeriodTypeId)
                ),
        },
        []
    );

    // Select first salary period type, if none selected
    useEffect(() => {
        if (!selectedSalaryPeriodTypeId && salaryPeriodTypes?.length > 0) {
            dispatch(
                updateWorkShiftPlanningFilters({
                    selectedSalaryPeriodTypeId: salaryPeriodTypes[0].id,
                })
            );
        }
    }, [salaryPeriodTypes]);

    useEffect(() => {
        if (!selectedSalaryPeriodId && !!salaryPeriods) {
            const salaryPeriod =
                salaryPeriods.find((p) =>
                    // Default to next salary period
                    Base.isAfter(p.startDate, new Date(), "day")
                ) || salaryPeriods[salaryPeriods.length - 1];

            if (salaryPeriod) {
                dispatch(
                    updateWorkShiftPlanningFilters({
                        selectedSalaryPeriodId: salaryPeriod.id,
                        startDate: salaryPeriod.startDate,
                        endDate: salaryPeriod.endDate,
                    })
                );
            }
        }
    }, [salaryPeriods, selectedSalaryPeriodId]);

    const salaryPeriodTypeChanged = useCallback(
        (id: string) => {
            if (!!id && id !== selectedSalaryPeriodTypeId) {
                dispatch(
                    updateWorkShiftPlanningFilters({
                        selectedSalaryPeriodTypeId: id,
                        selectedSalaryPeriodId: null,
                    })
                );
            }
        },
        [dispatch, selectedSalaryPeriodTypeId]
    );

    const salaryPeriodChanged = useCallback(
        (salaryPeriod: ISalaryPeriodItem) => {
            if (!salaryPeriod) {
                return;
            }
            dispatch(
                updateWorkShiftPlanningFilters({
                    selectedSalaryPeriodId: salaryPeriod.id,
                    startDate: salaryPeriod.startDate,
                    endDate: salaryPeriod.endDate,
                })
            );
        },
        [dispatch]
    );

    const handleEmployeesChange = (newValue: EmployeeListItemDto[]) => {
        dispatch(
            updateWorkShiftPlanningFilters({
                selectedEmployeeIds: newValue.map(employee => employee.id)
            })
        );
    };

    const showFilters = !!employeeGroups && !!salaryPeriodTypes;

    useEffect(() => {
        dispatch(
            updateWorkShiftPlanningFilters({ showUnemployable: true })
        );
    }, [dispatch]);

    return showFilters ? (
        <>
            <Grid2 xs={12} lg={3} maxWidth={280}>
                <FormControl variant="outlined" fullWidth={true}>
                    <Autocomplete
                        id="workshiftplanning-employeeGroup-select"
                        size="small"
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={selectedEmployeeGroup ?? null}
                        multiple={false}
                        fullWidth={true}
                        disableClearable={false}
                        noOptionsText={t("common.noOptions")}
                        options={employeeGroups || []}
                        getOptionLabel={(option: employeeGroupListItemDto) =>
                            option.name
                        }
                        onChange={(_, value: employeeGroupListItemDto) => {
                            dispatch(
                                updateWorkShiftPlanningFilters({
                                    selectedEmployeeGroupId: value
                                        ? value.id
                                        : null,
                                    selectedSalaryPeriodId: (value && value.salaryPeriodTypeId === selectedSalaryPeriodTypeId) ?
                                        selectedSalaryPeriodId :
                                        null,
                                    selectedSalaryPeriodTypeId: value
                                        ? value.salaryPeriodTypeId
                                        : selectedSalaryPeriodTypeId,
                                })
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("employeeGroup.employeeGroup")}
                            />
                        )}
                    />
                </FormControl>
            </Grid2>
            <Grid2 xs={12} lg={3} maxWidth={280}>
                <FormControl variant="outlined" fullWidth={true}>
                    <Autocomplete
                        id="workshiftplanning-employee-select"
                        size="small"
                        multiple
                        disableCloseOnSelect
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={selectedEmployees ?? []}
                        fullWidth={true}
                        disableClearable
                        noOptionsText={t("common.noOptions")}
                        options={employeeOptions || []}
                        getOptionLabel={(option: EmployeeListItemDto) =>
                            `${option?.lastName} ${option?.firstName}`
                        }
                        onChange={(_, value: EmployeeListItemDto[]) =>
                            handleEmployeesChange(value)
                        }
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    key={option.id}
                                    label={`${option?.lastName} ${option?.firstName}`}
                                    size="small"
                                    {...getTagProps({ index })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("employee.employees")}
                            />
                        )}
                    />
                </FormControl>
            </Grid2>
            <Grid2 xs={12} sm={6} md={3} lg={2}>
                <MuiSelect
                    size="small"
                    label={t("companySettings.salaryPeriodType")}
                    options={salaryPeriodTypes.map((t) => ({
                        value: t.id,
                        label: t.name,
                    }))}
                    value={selectedSalaryPeriodTypeId}
                    onChange={salaryPeriodTypeChanged}
                    disabled={!!selectedEmployeeGroup}
                />
            </Grid2>
            {!!salaryPeriods && (
                <Grid2 xs={12} lg="auto">
                    <SalaryPeriodSelect
                        size="small"
                        salaryPeriods={salaryPeriods}
                        selectedSalaryPeriod={selectedSalaryPeriod}
                        onChange={salaryPeriodChanged}
                    />
                </Grid2>
            )}
            <Grid2 xs={12} lg="auto">
                <FormControlLabel
                    control={
                        <Switch
                            checked={showUnemployable}
                            onChange={handleShowUnemployableChange}
                            color="primary"
                        />
                    }
                    label={t("workShiftPlanning.showUnassigned")}
                />
            </Grid2>
            {isEmployeeTab ? (
                <Grid2 xs={12} lg="auto">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showAllDayBookings}
                                onChange={handleShowAllDayBookingsChange}
                                color="primary"
                            />
                        }
                        label={t("workShiftPlanning.showAllDayBookings")}
                    />
                </Grid2>
            ) : null}
        </>
    ) : null;
};
